import { Listbox } from '@headlessui/react';
import { Fragment } from 'react';
import { Label, YStack, type YStackProps } from 'tamagui';
import CustomSelectedItemWrapper from './components/CustomSelectedItemWrapper';
import ListboxButton from './components/ListboxButton';
import ListboxOption from './components/ListboxOption';
import Text from '../Text/Text';
import ListboxOptions from './components/ListboxOptions';

export type Size = 'sm' | 'md';
type BaseItem = {
  id: string | number;
  label: string;
};
export type Variant = 'default' | 'rounded';

export type Props<TItem = BaseItem> = {
  size?: Size;
  items: Array<BaseItem & TItem>;
  selectedItemId?: BaseItem['id'];
  onSelect: (item: BaseItem & TItem) => void;
  placeholder: string;
  label?: string;
  loading?: boolean;
  variant?: Variant;
  centered?: boolean;
  wrapperProps?: YStackProps;
  testID?: YStackProps['testID'];
  error?: string;
  renderSelectedItem?: (item: BaseItem & TItem, meta: { isOpen: boolean }) => JSX.Element;
  renderItems?: (
    item: BaseItem & TItem,
    meta: { isActive: boolean; isSelected: boolean; isDisabled: boolean },
  ) => JSX.Element;
};

const Select = <TItem extends BaseItem>({
  size = 'md',
  items,
  selectedItemId,
  onSelect,
  placeholder,
  label,
  loading,
  variant = 'default',
  centered,
  wrapperProps,
  testID,
  error,
  renderSelectedItem,
  renderItems,
}: Props<TItem>) => {
  return (
    <YStack position="relative" flex={1}>
      <Listbox
        value={items.find((item) => item.id === selectedItemId) ?? null}
        onChange={onSelect}
        as={YStack}
        testID={testID}
        {...wrapperProps}
      >
        {({ open, value }) => (
          <>
            {label && (
              <Listbox.Button as={Fragment}>
                <Label cursor="pointer" fontSize="$10" color="$contrast" mb="$2">
                  {label}
                </Label>
              </Listbox.Button>
            )}

            {value ? (
              renderSelectedItem ? (
                <Listbox.Button as={CustomSelectedItemWrapper} isOpen={open}>
                  {renderSelectedItem(value, { isOpen: open })}
                </Listbox.Button>
              ) : (
                <Listbox.Button
                  as={ListboxButton}
                  size={size}
                  isOpen={open}
                  loading={loading}
                  variant={variant}
                  centered={centered}
                  error={Boolean(error)}
                >
                  {value.label}
                </Listbox.Button>
              )
            ) : (
              <Listbox.Button
                error={Boolean(error)}
                as={ListboxButton}
                size={size}
                isOpen={open}
                loading={loading}
                variant={variant}
                centered={centered}
              >
                {placeholder}
              </Listbox.Button>
            )}

            <Listbox.Options as={ListboxOptions}>
              {items.map((item) => (
                <Listbox.Option as={Fragment} key={item.id} value={item}>
                  {({ active, selected, disabled }) =>
                    renderItems ? (
                      <YStack tag="li">
                        <Text accessibilityRole="button" cursor="pointer">
                          {renderItems(item, {
                            isActive: active,
                            isSelected: selected,
                            isDisabled: disabled,
                          })}
                        </Text>
                      </YStack>
                    ) : (
                      <ListboxOption isActive={active} isSelected={selected} isDisabled={disabled}>
                        {item.label}
                      </ListboxOption>
                    )
                  }
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </>
        )}
      </Listbox>
      {error && (
        <Text color="$error" fontSize="$10" position="absolute" top="calc(100% + 4px)">
          {error}
        </Text>
      )}
    </YStack>
  );
};

export default Select;
